import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { resolveImageUrl } from '@/utils/image';
import InlineEnquiryForm from '@/components/InlineEnquiryForm';

const SiteFooter = lazy(() => import('@/components/SiteFooter'));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeIn = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  transition: { duration: 0.8 },
  viewport: { once: true, margin: '-50px' },
};

const Carousel = ({ images }) => {
  const [index, setIndex] = useState(0);
  if (images.length === 0) return (
    <div className="w-full h-64 bg-neutral-900 border border-white/10 flex items-center justify-center">
      <p className="text-gray-600 text-xs uppercase tracking-widest">No images</p>
    </div>
  );

  const prev = () => setIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  const next = () => setIndex((i) => (i === images.length - 1 ? 0 : i + 1));

  return (
    <div className="relative w-full h-72 md:h-80 overflow-hidden group">
      <img
        src={resolveImageUrl(images[index])}
        alt={`Slide ${index + 1}`}
        className="w-full h-full object-cover transition-opacity duration-500"
        loading="lazy"
        decoding="async"
      />
      {images.length > 1 && (
        <>
          <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity" data-testid="carousel-prev"><ChevronLeft size={20} /></button>
          <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity" data-testid="carousel-next"><ChevronRight size={20} /></button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, i) => (
              <button key={i} onClick={() => setIndex(i)} className={`w-2 h-2 rounded-full transition-colors ${i === index ? 'bg-white' : 'bg-white/30'}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const PackageCard = ({ pkg, index }) => {
  const anim = useMemo(() => ({
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay: index * 0.1 },
    viewport: { once: true, margin: '-50px' },
  }), [index]);

  return (
    <motion.div className="border border-white/10 overflow-hidden" {...anim} data-testid={`package-card-${index}`}>
      <Carousel images={pkg.images} />
      <div className="p-8">
        <div className="flex items-start justify-between gap-4 mb-4">
          <h3 className="text-xl md:text-2xl font-normal" style={{ fontFamily: 'Playfair Display, serif' }}>{pkg.name}</h3>
          <span className="text-lg md:text-xl font-medium shrink-0 text-white">{pkg.price}</span>
        </div>
        <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-line">{pkg.description}</p>
      </div>
    </motion.div>
  );
};

const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPackages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/packages`);
      setPackages(res.data);
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error('Packages fetch failed:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPackages(); }, [fetchPackages]);

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 lg:px-24">
      <motion.div {...fadeIn}>
        <h1 className="text-6xl md:text-8xl font-medium tracking-tight leading-none mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Packages</h1>
        <p className="text-sm tracking-widest uppercase text-gray-400 mb-16">Choose the package that suits your needs</p>

        {loading ? (
          <div className="text-center py-20"><p className="text-gray-400">Loading packages...</p></div>
        ) : packages.length === 0 ? (
          <div className="text-center py-20 border border-white/10">
            <p className="text-xl text-gray-400 mb-4">No packages available yet</p>
            <p className="text-sm text-gray-500">Check back soon for pricing and packages</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <PackageCard key={pkg.id} pkg={pkg} index={index} />
            ))}
          </div>
        )}
      </motion.div>
      <InlineEnquiryForm heading="Ready to Book?" subheading="Choose a package and get in touch to secure your date." />
      <Suspense fallback={null}><SiteFooter /></Suspense>
    </div>
  );
};

export default Packages;
